
import React from "react";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { ShieldAlert, User, Shield, Edit } from "lucide-react";
import { UserRole } from "@/utils/permissions/PermissionManager";

interface RoleBadgeProps {
  role: UserRole;
  showIcon?: boolean;
  showTooltip?: boolean;
}

const roleDescriptions = {
  admin: "Full administrative access to manage users and system settings",
  user: "Standard user with limited permissions",
  moderator: "Moderation capabilities for content and user submissions",
  editor: "Can create and edit content but cannot manage users"
};

export function RoleBadge({ role, showIcon = true, showTooltip = true }: RoleBadgeProps) {
  const getBadgeVariant = () => {
    switch (role) {
      case "admin": return "destructive";
      case "moderator": return "secondary";
      case "editor": return "default";
      default: return "default";
    }
  };

  const getIcon = () => {
    switch (role) {
      case "admin": return <ShieldAlert className="h-3 w-3" />;
      case "moderator": return <Shield className="h-3 w-3" />;
      case "editor": return <Edit className="h-3 w-3" />;
      default: return <User className="h-3 w-3" />;
    }
  };

  const badge = (
    <Badge 
      variant={getBadgeVariant() as any} 
      className="capitalize flex items-center gap-1.5"
    >
      {showIcon && getIcon()}
      {role}
    </Badge>
  );
  
  if (!showTooltip) return badge;
  
  return (
    <TooltipProvider>
      <Tooltip delayDuration={300}>
        <TooltipTrigger asChild>
          {badge}
        </TooltipTrigger>
        <TooltipContent className="text-xs">
          {roleDescriptions[role]}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
